<template>
  <TButtonGroup>
    <TGroupButton
      data="link"
      tooltip="Link"
      small
      toggle
      :active="editor.isActive('link')"
      :inverse="inverse"
      @button-clicked="openLinkModal"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 640 512"
        width="17"
        height="17"
      >
        <path
          d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z"
          fill="currentColor"
        />
      </svg>
    </TGroupButton>
  </TButtonGroup>
  <TModal
    v-if="showLinkModal"
    header="Link Settings"
    xs
    header-low
    no-min-height
    overflow-visible
    id="tiptap-link-modal"
    :inverse="inverse"
    @close="showLinkModal = false"
  >
    <template #icon>
      <LinkIcon class="h-8 w-8" />
    </template>
    <template #dialog-body>
      <div class="px-5 tiptap-link-modal">
        <content-row>
          <TMergetagsAutocomplete
            v-if="mergeTags"
            ref="linkInput"
            v-model="url"
            label="URL"
            placeholder="https://"
            :has-icon="hasUrlMergeTags"
            :inverse="inverse"
            :grouped-merge-tags="groupedUrlMergeTags"
            :flat-merge-tags="flatUrlMergeTags"
            :smart-merge-tags="{ enabled: true, syntax: mergeTags.syntax }"
            @on-keydown="onUrlKeydown"
          />
          <TInput
            v-else
            ref="linkInput"
            v-model="url"
            label="URL"
            placeholder="https://"
            :inverse="inverse"
            @enter-pressed="setLink()"
          />
        </content-row>
        <content-row>
          <TInput
            v-model="linkText"
            label="Preview text"
            class="box-border"
            :inverse="inverse"
            @enter-pressed="setLink()"
          />
        </content-row>
        <content-row>
          <TCheckBox v-model="targetBlank" label="Open in new tab" />
        </content-row>
      </div>
    </template>
    <template #dialog-footer>
      <div class="px-5 py-5 flex justify-between">
        <div>
          <TButton
            v-if="editor.isActive('link')"
            :inverse="inverse"
            @click="unsetLink()"
          >
            Unset
          </TButton>
        </div>
        <div class="flex gap-2">
          <TButton :inverse="inverse" @click="showLinkModal = false"
            >Cancel</TButton
          >
          <TButton success @click="setLink">Save</TButton>
        </div>
      </div>
    </template>
  </TModal>
</template>

<script setup lang="ts">
import { Editor } from "@tiptap/core";
import {
  TButton,
  TModal,
  TInput,
  TCheckBox,
  TButtonGroup,
  TGroupButton,
  TMergetagsAutocomplete,
} from "@topol/twig-ui";
import IEditorConfig from "../../../../types/IEditorConfig";
import {
  filterFullMergeTags,
  filterFullMergeTagsFlat,
} from "../../MTExtension/filterMergeTags";
import { Link as LinkIcon } from "@topol/icons";
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  useTemplateRef,
} from "vue";

const props = defineProps<{
  editor: Editor;
  inverse?: boolean;
  mergeTags?: Exclude<IEditorConfig["mergeTags"], undefined>;
}>();
const emits = defineEmits(["closeModal"]);

const groupedUrlMergeTags = computed(() =>
  props.mergeTags
    ? filterFullMergeTags(props.mergeTags.mergeTags, props.mergeTags.syntax)
    : []
);

const flatUrlMergeTags = computed(() =>
  props.mergeTags
    ? filterFullMergeTagsFlat(
        props.mergeTags.flatMergeTags,
        props.mergeTags.syntax
      )
    : []
);

// Guards the edge case where merge tags are configured but every one of them
// is filtered out (e.g. a config made entirely of HTML-snippet tags). Showing
// the purple button then would open an empty dropdown.
const hasUrlMergeTags = computed(
  () => groupedUrlMergeTags.value.length > 0 || flatUrlMergeTags.value.length > 0
);

const showLinkModal = ref(false);
const url = ref("");
const targetBlank = ref(false);
const linkText = ref("");
const originalLinkText = ref("");
const originalUrl = ref("");
const linkInput = useTemplateRef("linkInput");

function openLinkModal() {
  if (!props.editor) return;
  showLinkModal.value = true;
  const previousUrl = props.editor.getAttributes("link").href;
  const previousTarget = props.editor.getAttributes("link").target;
  targetBlank.value = previousTarget === "_blank" || !previousUrl;
  url.value = previousUrl || "";
  originalUrl.value = url.value;
  setTimeout(() => {
    if (linkInput.value) {
      const inputElement = linkInput.value.$el.querySelector("input");
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, 100);

  if (url.value !== "")
    props.editor.chain().focus().extendMarkRange("link").run();

  const { state } = props.editor;
  const { from, to, empty } = state.selection;

  if (!empty) {
    const selected = state.doc.textBetween(from, to);
    linkText.value = selected;
    originalLinkText.value = selected;
  } else {
    linkText.value = url.value || "";
    originalLinkText.value = "";
  }
}

// Receives real KeyboardEvents even though TInputWithIcon never emits
// "on-keydown": TMergetagsAutocomplete's @keydown binding is not a declared
// emit there, so it falls through as a native listener on the input's root
// div. Declaring "keydown" in TInputWithIcon's emits would break this.
function onUrlKeydown(event: KeyboardEvent) {
  if (event.key === "Enter") {
    event.preventDefault();
    setLink();
  }
}

function unsetLink() {
  if (!props.editor) return;
  props.editor.chain().focus().unsetLink().run();
  showLinkModal.value = false;
}

function setLink() {
  if (!props.editor) return;
  // cancelled
  if (url.value === null) {
    return;
  }

  // empty
  if (url.value === "") {
    props.editor.chain().focus().extendMarkRange("link").unsetLink().run();
    showLinkModal.value = false;
    return;
  }

  // update link
  const { state } = props.editor;
  const { empty } = state.selection;

  const chain = props.editor.chain().focus();

  if (empty && originalUrl.value === "") {
    // vlož nový text s odkazem
    chain.insertContent([
      {
        type: "text",
        text: linkText.value || url.value,
        marks: [
          {
            type: "link",
            attrs: {
              href: url.value,
              target: targetBlank.value ? "_blank" : "",
            },
          },
        ],
      },
    ]);
  } else {
    if (linkText.value !== originalLinkText.value) {
      // Nahraď selection novým textem S MARKEM
      chain.insertContent([
        {
          type: "text",
          text: linkText.value,
          marks: [
            {
              type: "link",
              attrs: {
                href: url.value,
                target: targetBlank.value ? "_blank" : "",
              },
            },
          ],
        },
      ]);
    } else {
      // Jen nastav mark na aktuální rozsah
      chain.setLink({
        href: url.value,
        target: targetBlank.value ? "_blank" : "",
      });
    }
  }

  chain.run();
  showLinkModal.value = false;
}

watch(showLinkModal, () => {
  if (!showLinkModal.value) {
    emits("closeModal");
  }
});

watch(url, (newUrl) => {
  if (originalLinkText.value === "") {
    linkText.value = newUrl;
  }
});

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
    event.preventDefault();
    openLinkModal();
  }
}

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
});
</script>

<style>
.tiptap-link-modal * {
  box-sizing: border-box;
}
</style>
